import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Clock, Users, BookOpen, Pencil,
  CheckCircle, XCircle, Heart, AlertTriangle, ChevronRight, ArrowLeft
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WORKSHOP_ID = 'book-journaling-midnight-library';
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

const BookJournalingWorkshop = () => {
  const [showRegistration, setShowRegistration] = useState(false);
  const [workshop, setWorkshop] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', age: '', sex: '', has_read_book: false, quantity: 1, partner_email: '' });
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  const openRegistration = () => {
    setShowRegistration(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeRegistration = () => {
    setShowRegistration(false);
    setRegistered(false);
    setFormData({ name: '', email: '', phone: '', age: '', sex: '', has_read_book: false, quantity: 1, partner_email: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    fetchWorkshop();
  }, []);

  const fetchWorkshop = async () => {
    try {
      const res = await axios.get(`${API}/workshop/${WORKSHOP_ID}`);
      setWorkshop(res.data);
    } catch (err) {
      console.error('Failed to fetch workshop:', err);
    }
  };

  const seatsRemaining = workshop?.seats_remaining ?? 8;
  const soldOut = workshop?.sold_out ?? false;
  const maxSeats = workshop?.max_seats ?? 8;
  const pricePerSeat = workshop?.price_per_seat ?? 999;
  const pairPricePerSeat = workshop?.pair_price_per_seat ?? 899;
  const canBookTwo = seatsRemaining >= 2;
  const currentPricePerSeat = formData.quantity === 2 ? pairPricePerSeat : pricePerSeat;
  const totalPrice = currentPricePerSeat * formData.quantity;
  const ageUnder18 = formData.age && parseInt(formData.age) < 18;

  const handlePayment = async () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.age || !formData.sex) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.has_read_book) {
      toast.error('Please confirm whether you have read the book');
      return;
    }
    setLoading(true);
    try {
      const orderRes = await axios.post(`${API}/workshop/${WORKSHOP_ID}/create-order`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        age: parseInt(formData.age),
        sex: formData.sex,
        has_read_book: formData.has_read_book,
        quantity: formData.quantity,
        partner_email: formData.quantity === 2 ? formData.partner_email : '',
      });
      const options = {
        key: RAZORPAY_KEY,
        amount: orderRes.data.amount,
        currency: orderRes.data.currency,
        name: 'Psychotherapy with Anvita',
        description: `Book Journaling Workshop \u2014 ${formData.quantity} seat${formData.quantity > 1 ? 's' : ''}`,
        order_id: orderRes.data.order_id,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(`${API}/workshop/${WORKSHOP_ID}/verify-payment`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              temp_id: orderRes.data.temp_id,
            });
            setRegistered(true);
            toast.success(verifyRes.data.message);
            fetchWorkshop();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Verification failed. Please contact support.');
          } finally {
            setLoading(false);
          }
        },
        prefill: { name: formData.name, email: formData.email },
        theme: { color: '#4A6741' },
        modal: { ondismiss: () => setLoading(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create order. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="workshop-page">

      {showRegistration ? (
      /* ========== Full-Page Registration Form ========== */
      <div className="pt-20 pb-20 px-6 md:px-12" data-testid="registration-page">
        <div className="max-w-md mx-auto">

          {/* Back button */}
          <button
            onClick={closeRegistration}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8 mt-4"
            data-testid="reg-back-button"
          >
            <ArrowLeft size={16} />
            Back to workshop
          </button>

          {!registered ? (
            <div>
              <h1 className="text-3xl sm:text-4xl font-heading font-medium tracking-tight text-primary mb-2" data-testid="reg-title">
                Reserve Your Spot
              </h1>
              <p className="text-sm text-muted-foreground mb-8">
                {soldOut
                  ? 'This workshop is currently sold out.'
                  : `${seatsRemaining} of ${maxSeats} seats remaining`}
              </p>

              {soldOut ? (
                <div className="p-6 rounded-xl text-center bg-secondary">
                  <AlertTriangle size={32} className="mx-auto mb-2 text-destructive" />
                  <p className="font-medium text-foreground">This cohort is full</p>
                  <p className="text-sm mt-1 text-muted-foreground">Check back for future cohorts!</p>
                </div>
              ) : (
                <div className="space-y-5">
                  <div>
                    <Label className="text-sm font-semibold text-primary">Full Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Your name"
                      className="mt-1.5 rounded-xl"
                      data-testid="reg-name"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-primary">Phone Number</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                      className="mt-1.5 rounded-xl"
                      data-testid="reg-phone"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-primary">Email Address</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="your@email.com"
                      className="mt-1.5 rounded-xl"
                      data-testid="reg-email"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm font-semibold text-primary">Age</Label>
                      <Input
                        type="number"
                        min="1"
                        max="120"
                        value={formData.age}
                        onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                        placeholder="25"
                        className="mt-1.5 rounded-xl"
                        data-testid="reg-age"
                      />
                      {ageUnder18 && (
                        <p className="mt-1.5 text-xs font-medium text-destructive" data-testid="age-warning">
                          This workshop is only open to participants aged 18 and above.
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-primary">Sex</Label>
                      <select
                        value={formData.sex}
                        onChange={(e) => setFormData(prev => ({ ...prev, sex: e.target.value }))}
                        className="mt-1.5 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm text-foreground"
                        data-testid="reg-sex"
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Non-Binary">Non-Binary</option>
                        <option value="Prefer not to say">Prefer not to say</option>
                      </select>
                    </div>
                  </div>

                  {/* Seat Quantity Selector */}
                  <div>
                    <Label className="text-sm font-semibold text-primary">Number of Seats</Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, quantity: 1 }))}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          formData.quantity === 1
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/30'
                        }`}
                        data-testid="seat-select-1"
                      >
                        <p className="font-semibold text-foreground text-sm">1 Seat</p>
                        <p className="text-xl font-bold text-primary mt-1">{'\u20B9'}{pricePerSeat}</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => canBookTwo && setFormData(prev => ({ ...prev, quantity: 2 }))}
                        disabled={!canBookTwo}
                        className={`p-4 rounded-xl border-2 text-left transition-all relative overflow-hidden ${
                          !canBookTwo
                            ? 'border-border opacity-50 cursor-not-allowed'
                            : formData.quantity === 2
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/30'
                        }`}
                        data-testid="seat-select-2"
                      >
                        <span className="absolute top-0 right-0 bg-accent text-white text-[10px] px-2 py-0.5 rounded-bl-lg font-bold tracking-wide">
                          1+1
                        </span>
                        <p className="font-semibold text-foreground text-sm">2 Seats</p>
                        <p className="text-xl font-bold text-primary mt-1">
                          {'\u20B9'}{pairPricePerSeat} <span className="text-xs font-normal text-muted-foreground">each</span>
                        </p>
                        <p className="text-xs text-accent font-semibold mt-0.5">
                          Save {'\u20B9'}{(pricePerSeat - pairPricePerSeat) * 2}!
                        </p>
                      </button>
                    </div>
                  </div>

                  {/* Partner Email (only for 2-seat bookings) */}
                  {formData.quantity === 2 && (
                    <div>
                      <Label className="text-sm font-semibold text-primary">Partner's Email Address</Label>
                      <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">Your companion will receive the calendar invite and workshop details</p>
                      <Input
                        type="email"
                        value={formData.partner_email}
                        onChange={(e) => setFormData(prev => ({ ...prev, partner_email: e.target.value }))}
                        placeholder="companion@email.com"
                        className="rounded-xl"
                        data-testid="reg-partner-email"
                      />
                    </div>
                  )}

                  {/* Have you read the book? */}
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-secondary">
                    <input
                      type="checkbox"
                      checked={formData.has_read_book}
                      onChange={(e) => setFormData(prev => ({ ...prev, has_read_book: e.target.checked }))}
                      className="mt-1 w-4 h-4 rounded accent-primary"
                      data-testid="reg-read-book-checkbox"
                    />
                    <label className="text-sm leading-relaxed text-muted-foreground">
                      I have read <em className="font-medium text-foreground">The Midnight Library</em> by Matt Haig
                    </label>
                  </div>

                  {/* Price Summary */}
                  <div className="p-4 rounded-xl bg-secondary text-center" data-testid="price-summary">
                    <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1">Total</p>
                    <p className="text-2xl font-heading font-semibold text-primary">
                      {'\u20B9'}{totalPrice.toLocaleString('en-IN')}
                    </p>
                    {formData.quantity === 2 && (
                      <p className="text-xs text-accent font-semibold mt-1">
                        {'\u20B9'}{pairPricePerSeat} &times; 2 seats &mdash; 1+1 offer applied!
                      </p>
                    )}
                  </div>

                  <Button
                    onClick={handlePayment}
                    disabled={loading || !formData.name || !formData.email || !formData.phone || !formData.age || !formData.sex || !formData.has_read_book || ageUnder18}
                    className="w-full rounded-full py-6 text-base font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="reg-pay-button"
                  >
                    {loading ? 'Processing...' : `Pay \u20B9${totalPrice.toLocaleString('en-IN')} & Reserve`}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    You'll receive a calendar invite with the Google Meet link after payment.
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* ========== Booking Confirmation ========== */
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-primary/10">
                  <CheckCircle size={36} className="text-primary" />
                </div>
                <h1 className="text-3xl font-heading font-medium text-primary">Booking Confirmed!</h1>
                <p className="text-sm text-muted-foreground mt-1">Your spot has been reserved</p>
              </div>

              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-secondary">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase">Workshop</p>
                      <p className="font-medium text-foreground">Book Journaling</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase">Date & Time</p>
                      <p className="font-medium text-foreground">{workshop?.date || 'Aug 9'}, {workshop?.time || '11 AM IST'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase">Seats</p>
                      <p className="font-medium text-foreground">{formData.quantity} seat{formData.quantity > 1 ? 's' : ''}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase">Amount Paid</p>
                      <p className="font-medium text-primary">{'\u20B9'}{totalPrice.toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-border bg-card">
                  <p className="text-sm font-semibold text-foreground mb-2">What happens next?</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle size={14} className="text-primary mt-0.5 flex-shrink-0" />
                      <span>A <strong className="text-foreground">welcome email</strong> with all workshop details has been sent to your inbox</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={14} className="text-primary mt-0.5 flex-shrink-0" />
                      <span>You will receive a <strong className="text-foreground">Google Calendar invite</strong> with the meeting link</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={14} className="text-primary mt-0.5 flex-shrink-0" />
                      <span>Keep a journal, pen, and colours handy for the session</span>
                    </li>
                  </ul>
                </div>

                <div className="p-3 rounded-xl bg-primary/5 text-center">
                  <p className="text-xs text-muted-foreground">Questions? Reach out at</p>
                  <p className="text-sm font-medium text-primary">+91-96502 54989</p>
                </div>
              </div>

              <Button
                onClick={closeRegistration}
                className="w-full mt-6 rounded-full py-5 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="reg-close-button"
              >
                Back to Workshop
              </Button>
            </div>
          )}
        </div>
      </div>
      ) : (
      /* ========== Landing Page ========== */
      <>

      {/* Hero Section */}
      <section className="pt-28 pb-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-semibold tracking-widest uppercase mb-4 text-primary/70">
              A Reflective Experience
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight tracking-tight mb-6 text-primary">
              What if finishing the book was just the beginning?
            </h1>
            <p className="text-lg leading-relaxed mb-4 text-muted-foreground">
              Some books don't end on the last page. They linger in our thoughts, raise new questions, and invite us to see ourselves differently.
            </p>
            <p className="text-base leading-relaxed mb-4 text-muted-foreground">
              Join in for a reflective journaling experience centred around{' '}
              <em className="font-medium text-primary">The Midnight Library</em> by Matt Haig.
            </p>
            <p className="text-base leading-relaxed mb-8 text-muted-foreground">
              I have always found myself thinking about my own life after a good book has finished. That's the entire idea behind this space. Join us for a 120-minute online reflective book journaling workshop. Together, we will gently explore our own parallel paths, choices, and regrets.
            </p>
            <Button
              onClick={() => openRegistration()}
              disabled={soldOut}
              className={`rounded-full px-8 py-6 text-base font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                soldOut ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
              data-testid="hero-cta"
            >
              {soldOut ? 'Sold Out' : `Reserve Your Spot (${maxSeats} Seats Only)`}
              {!soldOut && <ChevronRight size={18} className="ml-2" />}
            </Button>
            {!soldOut && seatsRemaining <= 3 && seatsRemaining > 0 && (
              <p className="mt-3 text-sm font-medium text-destructive" data-testid="urgency-text">
                Only {seatsRemaining} seat{seatsRemaining === 1 ? '' : 's'} remaining!
              </p>
            )}
          </div>
          <div className="relative">
            <img
              src="https://customer-assets-agu9un31.emergentagent.net/job_26b57b05-6861-4696-b587-c466b74ca7ab/artifacts/tx0hgww9_Book%20Journaling%20Workshop%20%282%29.webp"
              alt="Book Journaling Workshop - The Midnight Library by Matt Haig"
              className="rounded-3xl w-full object-contain shadow-lg max-h-[350px] md:max-h-[520px]"
            />
          </div>
        </div>
      </section>

      {/* What is Book Journaling */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-secondary">
        <div className="max-w-7xl mx-auto grid md:grid-cols-5 gap-12 items-center">
          <div className="md:col-span-2">
            <img
              src="https://images.unsplash.com/photo-1775129667571-7fa3f51c05e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxqb3VybmFsaW5nJTIwd2FybSUyMHdyaXRpbmclMjBkZXNrfGVufDB8fHx8MTc4NTA3MTM4OXww&ixlib=rb-4.1.0&q=85&w=800"
              alt="Warm journaling setup with open notebook"
              className="rounded-3xl w-full object-cover shadow-lg max-h-[400px]"
            />
          </div>
          <div className="md:col-span-3">
            <h2 className="text-3xl sm:text-4xl font-medium tracking-tight mb-6 text-primary">
              What is Book Journaling?
            </h2>
            <p className="text-base leading-relaxed mb-4 text-muted-foreground">
              Have you ever felt that a book left you with more questions about life when you finished reading it? Me too.
            </p>
            <p className="text-base leading-relaxed mb-6 text-muted-foreground">
              Book journaling is a unique experience in which a small group of people who have read the same book come together to reflect on core themes of the tale. It is a structured process of writing down one's own thoughts, emotions, and perceptions to build deep clarity in life. This is achieved through unique journal prompts, art, and a space for shame-free discussion.
            </p>
            <blockquote className="border-l-4 border-primary pl-6 py-4 my-6 rounded-r-xl bg-primary/5">
              <p className="text-base leading-relaxed italic text-muted-foreground">
                "We're starting this journey with <em>The Midnight Library</em> by Matt Haig, a book about a woman who gets the rare, strange gift of trying on every life she didn't live. Most of us don't get a midnight library. But we do have a few hours together, a journal, and the chance to help us feel more connected with ourselves."
              </p>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Setting the Container */}
      <section className="py-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight mb-12 text-center text-primary">
            Setting the Container
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 rounded-3xl bg-card border border-border">
              <h3 className="text-xl font-semibold mb-6 flex items-center gap-3 text-primary">
                <CheckCircle size={24} /> What to Expect
              </h3>
              <ul className="space-y-4">
                {[
                  { title: 'Intimate Atmosphere', desc: 'A small group of 8 or fewer people to ensure everyone has space.' },
                  { title: 'Guided Prompts', desc: 'Reflective journaling questions specifically based on The Midnight Library.' },
                  { title: 'Your Own Pace', desc: "A shame-free space to share as little or as much as you'd like." }
                ].map(item => (
                  <li key={item.title} className="flex gap-3">
                    <CheckCircle size={18} className="mt-0.5 flex-shrink-0 text-primary" />
                    <div>
                      <span className="font-semibold text-sm text-primary">{item.title}</span>
                      <p className="text-sm leading-relaxed text-muted-foreground">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
            <Card className="p-8 rounded-3xl bg-card border border-border">
              <h3 className="text-xl font-semibold mb-6 flex items-center gap-3 text-muted-foreground">
                <XCircle size={24} /> What This Is Not
              </h3>
              <ul className="space-y-4">
                {[
                  { title: 'Not Therapy', desc: 'This is a reflective workshop, not a substitute for regular therapy or counselling.' },
                  { title: 'Not Crisis Support', desc: 'We cannot safely explore severe trauma or crisis in a group setting.' },
                  { title: 'Not a Review Club', desc: "We won't debate plot points or writing styles." }
                ].map(item => (
                  <li key={item.title} className="flex gap-3">
                    <XCircle size={18} className="mt-0.5 flex-shrink-0 text-muted-foreground/50" />
                    <div>
                      <span className="font-semibold text-sm text-foreground/70">{item.title}</span>
                      <p className="text-sm leading-relaxed text-muted-foreground">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Workshop Details */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-secondary">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight mb-4 text-center text-primary">
            Workshop Details
          </h2>

          {/* Prominent Date & Time */}
          <div className="text-center mb-10">
            <p className="text-2xl sm:text-3xl font-heading font-medium text-primary mt-2">
              {workshop?.date || 'Date TBA'}
            </p>
            <p className="text-lg text-foreground mt-1">{workshop?.time || 'Time TBA'}</p>
            <p className="text-sm text-muted-foreground mt-1">Online via Google Meet &mdash; private link emailed after registration</p>
          </div>

          {/* Detail Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Clock, label: 'Duration', value: '120 Minutes', sub: 'Of reflective journaling' },
              { icon: Users, label: 'Capacity', value: `${maxSeats} Seats Max`, sub: 'Intimate group setting' },
              { icon: BookOpen, label: 'Who Can Join', value: 'Adults (18+)', sub: 'Who have read the book' },
              { icon: Pencil, label: 'What to Bring', value: 'Journal & Colours', sub: 'Notebook, pen, crayons' },
            ].map(item => (
              <Card key={item.label} className="p-6 rounded-2xl text-center bg-card border border-border">
                <item.icon size={28} className="mx-auto mb-3 text-primary" />
                <p className="text-xs font-semibold tracking-widest uppercase mb-1 text-muted-foreground">{item.label}</p>
                <p className="text-base font-semibold text-foreground">{item.value}</p>
                <p className="text-xs mt-1 text-muted-foreground">{item.sub}</p>
              </Card>
            ))}
          </div>

          {/* Pricing Highlight */}
          <div className="mt-10 p-8 rounded-3xl bg-card border border-border text-center" data-testid="pricing-section">
            <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-3">Pricing</p>
            <p className="text-3xl font-heading font-semibold text-primary">
              {'\u20B9'}{pricePerSeat} <span className="text-base font-body font-normal text-muted-foreground">per seat</span>
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent/10">
              <Heart size={16} className="text-accent" />
              <span className="text-sm font-semibold text-accent">
                Bring a friend! 1+1 Offer: {'\u20B9'}{pairPricePerSeat} each — Save {'\u20B9'}{(pricePerSeat - pairPricePerSeat) * 2}!
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* About Facilitator */}
      <section className="py-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-4xl mx-auto grid md:grid-cols-5 gap-12 items-center">
          <div className="md:col-span-2 flex justify-center">
            <img
              src="https://customer-assets-agu9un31.emergentagent.net/job_26b57b05-6861-4696-b587-c466b74ca7ab/artifacts/i8aat9dg_IMG-20260629-WA0002.jpg"
              alt="Anvita Sethi - Workshop Facilitator"
              className="rounded-3xl w-full max-w-xs object-cover shadow-lg"
            />
          </div>
          <div className="md:col-span-3">
            <h2 className="text-3xl sm:text-4xl font-medium tracking-tight mb-6 text-primary">
              Heya! I am Anvita.
            </h2>
            <p className="text-base leading-relaxed mb-4 text-muted-foreground">
              I've been working as a Psychotherapist and a Mental Health Professional for over a decade now. I am also the Co-founder of Untamed, a women's community working with Body Image Concerns. In my private practice, I work deeply with adults, couples, and adolescents, but this workshop comes from a slightly different, deeply personal place.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              Books have always left me with more questions than answers, in the best way possible. <em>The Midnight Library</em> did that for me, and that's when I decided to combine my reflective skills as a therapist with the books we read. Let's meet each other in the workshop!
            </p>
          </div>
        </div>
      </section>

      {/* Mental Health Disclaimer */}
      <section className="py-12 px-6 md:px-12 lg:px-20">
        <div className="max-w-4xl mx-auto p-8 rounded-3xl bg-secondary border border-accent/20">
          <div className="flex gap-4">
            <Heart size={24} className="flex-shrink-0 mt-1 text-destructive" />
            <div>
              <h3 className="text-xl font-semibold mb-3 text-foreground">
                A Gentle Note on Mental Health
              </h3>
              <p className="text-sm leading-relaxed mb-4 text-muted-foreground">
                If you've read the book, you would understand the protagonist's journey with depression. The book has mentions of self-harm, suicide and death, themes that can be heavy to experience and as a psychologist, it is my duty to inform you that one book journaling workshop isn't enough to explore these sensitive topics. If you are dealing with these emotions, please seek help from a Psychiatrist/Psychologist.
              </p>
              <p className="text-sm font-bold text-destructive">
                For immediate assistance with a mental health crisis, please contact the Vandrevala Foundation Emergency Helpline: +91-9999-666-555.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-20 px-6 md:px-12 lg:px-20 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight mb-6 text-primary">
            Ready to explore yourself through this book?
          </h2>
          <Button
            onClick={() => setShowRegistration(true)}
            disabled={soldOut}
            className={`rounded-full px-10 py-7 text-lg font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg ${
              soldOut ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
            data-testid="footer-cta"
          >
            {soldOut ? 'Sold Out' : 'Secure My Spot Today'}
            {!soldOut && <ChevronRight size={20} className="ml-2" />}
          </Button>
        </div>
      </section>

      </>
      )}
    </div>
  );
};

export default BookJournalingWorkshop;
